/**
 * audio/index.js — the procedural audio engine (GDD 11).
 *
 * Every sound in Carnival Chaos is synthesised in code. There are no sample
 * files, no loaders and no network requests: recipes in `sfx-library.js`
 * schedule Web Audio nodes, `music.js` generates a carnival waltz bar by bar,
 * and `ambient.js` layers a procedural midway bed underneath both.
 *
 * ── The interface contract (unchanged from the stub) ──
 *   audio.play(eventName, opts)     opts: { position?, volume?, pitch?, delay?,
 *                                           pan?, speed?, points?, material?,
 *                                           bus?, sustain? }
 *   audio.stop(eventName)
 *   audio.setListener(object3D)
 *   audio.setVolume(bus, value)     bus: 'master'|'sfx'|'music'|'ambient'
 *   audio.isReady()
 *   ambient.start(sceneName) / ambient.stop()
 *   music.play(trackId, opts) / music.stop(fadeSeconds)
 *   install()
 *
 * ── Two things worth knowing before you change anything here ──
 *
 * **Test mode is silent by construction.** Under `?test=1` no AudioContext is
 * ever created; play calls become entries in a ring buffer that tests assert
 * against via `__cc.audio.played()`. That is why headless runs are fast and
 * produce no autoplay warnings.
 *
 * **Stalls do not have to call in.** This module subscribes to the gameplay
 * events directly — STALL_SCORE, THROW, GRAB, TICKETS_SPENT, DIRECT_PRIZE and
 * the rest — so a new stall makes the right noises without touching audio at
 * all. The handful of `audio.play` calls already in `stalls/` therefore arrive
 * alongside an event carrying the same meaning, and the de-dupe window folds
 * the pair into one sound, keeping whichever payload is richer.
 *
 * @module audio
 */

import { EVENTS } from '../constants.js';
import { bus } from '../core/events.js';
import { RECIPES, resolve, recipeNames, aliasNames, materialNames } from './sfx-library.js';
import { aseed } from './synth.js';
import * as ctxmod from './context.js';
import * as ambientMod from './ambient.js';
import * as musicMod from './music.js';
import * as spatial from './spatial.js';

/** Names that are routed to a subsystem rather than to a recipe. */
const ROUTED = Object.freeze({
  'space-music': (o) => musicMod.play(o.track || 'space-calm', o),
  music: (o) => musicMod.play(o.track || 'hub-day', o),
  'music-stop': () => musicMod.stop(),
  'ambient-carnival': (o) => ambientMod.start(o.scene || 'hub'),
  ambient: (o) => ambientMod.start(o.scene || 'hub'),
  'ambient-space': () => ambientMod.start('personal-space'),
});

/** Live sustained voices, keyed by recipe name, so `stop()` can find them. */
const sustained = new Map();

let installed = false;

// ─────────────────────────────────────────────────────────────────────────────
// The play path
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Internal option keys that should never reach the played log.
 * @param {object} o
 */
function publicOpts(o) {
  const out = {};
  for (const k of Object.keys(o)) {
    if (k.startsWith('_') || k === 'position') continue;
    out[k] = o[k];
  }
  return out;
}

/**
 * Play a sound.
 *
 * @param {string} eventName GDD 11.2 name, canonical recipe name or shorthand.
 * @param {object} [opts]
 */
function play(eventName, opts = {}) {
  if (!eventName) return;

  const routed = ROUTED[String(eventName).toLowerCase()];
  if (routed) { routed(opts); return; }

  const key = resolve(eventName);
  if (!key) {
    // Log the attempt anyway: a test asserting "the game tried to make a noise"
    // should not silently pass just because no recipe exists yet.
    ctxmod.noteUnknown(eventName);
    ctxmod.logPlay(eventName, publicOpts(opts));
    return;
  }

  const rec = RECIPES[key];

  // De-dupe. A repeat inside the window is folded into the first entry rather
  // than dropped, so whichever call carried `speed` or `points` wins — the
  // direct call from `stalls/` and the bus event arrive microseconds apart and
  // only one of them is the informative one.
  if (rec.dedupe > 0) {
    const prev = ctxmod.lastPlay(key, rec.dedupe);
    if (prev) {
      Object.assign(prev.opts, publicOpts(opts));
      return;
    }
  }

  const o = opts.position ? spatial.apply(opts) : opts;
  ctxmod.logPlay(key, publicOpts(o));

  if (ctxmod.LOG_ONLY || !ctxmod.isUnlocked()) return;
  const volume = o.volume != null ? o.volume : 1;
  if (volume <= 0.012) return;                     // out of earshot — do no work
  if (!ctxmod.takeVoice(rec.dur)) return;          // polyphony budget exhausted

  schedule(key, rec, o, volume);
}

/**
 * Build the node chain for one play and hand it to the recipe.
 * @private
 */
function schedule(key, rec, o, volume) {
  const ctx = ctxmod.context();
  const dest = ctxmod.busNode(o.bus || rec.bus);
  if (!ctx || !dest) return;

  try {
    const gain = ctx.createGain();
    gain.gain.value = Math.max(0, Math.min(2, volume));
    gain.connect(dest);

    const t0 = ctx.currentTime + Math.max(0, o.delay || 0);
    const args = o.sustain ? { ...o, duration: 60 } : o;
    const end = rec.fn(ctx, gain, args, t0);

    if (o.sustain) {
      stopSustained(key, 0.05);
      sustained.set(key, { gain, ctx });
      return;
    }
    const ms = Math.max(60, (end - ctx.currentTime) * 1000 + 300);
    setTimeout(() => { try { gain.disconnect(); } catch { /* gone */ } }, Math.min(30_000, ms));
  } catch (err) {
    ctxmod.reportError(err);
  }
}

/**
 * Fade out and release a sustained voice.
 * @private
 */
function stopSustained(key, seconds = 0.3) {
  const h = sustained.get(key);
  if (!h) return;
  sustained.delete(key);
  try {
    const t = h.ctx.currentTime;
    h.gain.gain.cancelScheduledValues(t);
    h.gain.gain.setValueAtTime(Math.max(0.0001, h.gain.gain.value), t);
    h.gain.gain.linearRampToValueAtTime(0, t + seconds);
    setTimeout(() => { try { h.gain.disconnect(); } catch { /* gone */ } }, seconds * 1000 + 200);
  } catch (err) {
    ctxmod.reportError(err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Public surface — shape preserved from the stub
// ─────────────────────────────────────────────────────────────────────────────

export const audio = {
  /**
   * @param {string} eventName See GDD 11.2.
   * @param {{ position?: object, volume?: number, pitch?: number, delay?: number,
   *           pan?: number, speed?: number, points?: number, material?: string,
   *           bus?: string, sustain?: boolean }} [opts]
   */
  play(eventName, opts = {}) {
    play(eventName, opts);
  },

  /** @param {string} eventName Stops a sustained voice, or the music. */
  stop(eventName) {
    const key = resolve(eventName) || String(eventName || '');
    ctxmod.logPlay('@stop', { name: key });
    if (key === 'music') { musicMod.stop(); return; }
    if (key === 'ambient') { ambientMod.stop(); return; }
    stopSustained(key);
  },

  /** @param {import('three').Object3D} object3D Usually the camera. */
  setListener(object3D) {
    spatial.setListener(object3D);
  },

  /**
   * @param {'master'|'sfx'|'music'|'ambient'} channel
   * @param {number} value 0–1. Persisted to `save.settings`.
   */
  setVolume(channel, value) {
    ctxmod.setVolume(channel, value);
  },

  /** @returns {boolean} True once a live AudioContext is running. */
  isReady() {
    return ctxmod.isReady();
  },

  /**
   * Bring audio up from inside a gesture handler. Normally unnecessary — the
   * gesture listeners installed by `install()` handle it.
   * @returns {Promise<boolean>}
   */
  unlock() {
    return ctxmod.unlock();
  },

  /** @returns {string[]} Every implemented recipe name. */
  recipes() {
    return recipeNames();
  },
};

export const ambient = {
  /** @param {string} sceneName 'hub' | 'stall' | 'personal-space' | 'none' */
  start(sceneName) {
    ambientMod.start(sceneName);
  },
  stop() {
    ambientMod.stop();
  },
};

export const music = {
  /** @param {string} trackId @param {{ loop?: boolean, fadeIn?: number }} [opts] */
  play(trackId, opts = {}) {
    musicMod.play(trackId, opts);
  },
  /** @param {number} [fadeSeconds=1] */
  stop(fadeSeconds = 1) {
    musicMod.stop(fadeSeconds);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Offline rendering — the proof that a recipe actually makes a sound
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Render one recipe into an OfflineAudioContext and measure it.
 *
 * A recipe that throws, or that schedules a graph which never reaches the
 * destination, is silent in a way no event-log assertion can catch. This is how
 * the suite proves each of them is audible.
 *
 * @param {string} name
 * @param {object} [opts] Recipe options, plus `seconds` to override length.
 * @returns {Promise<{ name: string, ok: boolean, peak: number, rms: number,
 *                     duration: number, reason?: string }>}
 */
export async function renderOffline(name, opts = {}) {
  const key = resolve(name);
  if (!key) return { name, ok: false, peak: 0, rms: 0, duration: 0, reason: 'unknown-recipe' };

  const OC = typeof window !== 'undefined'
    ? (window.OfflineAudioContext || window.webkitOfflineAudioContext)
    : null;
  if (!OC) return { name: key, ok: false, peak: 0, rms: 0, duration: 0, reason: 'no-offline-context' };

  const rec = RECIPES[key];
  const sampleRate = 44100;
  const seconds = Math.max(0.2, (opts.seconds != null ? opts.seconds : rec.dur) + 0.4);

  let buffer;
  try {
    const ctx = new OC(2, Math.ceil(sampleRate * seconds), sampleRate);
    // Fixed seed so a render is reproducible run to run.
    aseed(0x51ee7 + key.length);
    rec.fn(ctx, ctx.destination, { duration: Math.min(rec.dur, 1.2), ...opts }, 0);
    buffer = await ctx.startRendering();
  } catch (err) {
    return {
      name: key, ok: false, peak: 0, rms: 0, duration: 0,
      reason: `threw: ${err && err.message ? err.message : err}`,
    };
  }

  let peak = 0;
  let sumsq = 0;
  let count = 0;
  let lastAudible = 0;
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const d = buffer.getChannelData(ch);
    for (let i = 0; i < d.length; i++) {
      const v = Math.abs(d[i]);
      if (v > peak) peak = v;
      sumsq += d[i] * d[i];
      count++;
    }
  }
  const threshold = Math.max(1e-4, peak * 0.02);
  for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
    const d = buffer.getChannelData(ch);
    for (let i = d.length - 1; i > lastAudible; i--) {
      if (Math.abs(d[i]) > threshold) { lastAudible = i; break; }
    }
  }

  return {
    name: key,
    ok: true,
    peak,
    rms: Math.sqrt(sumsq / Math.max(1, count)),
    duration: lastAudible / sampleRate,
    rendered: seconds,
  };
}

/**
 * Render every recipe. Sequential on purpose — a dozen concurrent offline
 * contexts is a good way to make a headless browser fall over.
 * @returns {Promise<Array<object>>}
 */
export async function renderAll(opts = {}) {
  const results = [];
  for (const name of recipeNames()) {
    // eslint-disable-next-line no-await-in-loop
    results.push(await renderOffline(name, opts));
  }
  return results;
}

// ─────────────────────────────────────────────────────────────────────────────
// Install — event wiring and the test surface
// ─────────────────────────────────────────────────────────────────────────────

/** @private Distance a release has to exceed before it counts as a throw. */
const THROW_SPEED = 1.6;

/** @private Subscribe to the gameplay events that should make a noise. */
function wireEvents() {
  bus.on(EVENTS.AUDIO_PLAY, ({ name, ...opts } = {}) => {
    if (name) play(name, opts);
  });

  // ── Handling and throwing ────────────────────────────────────────────────
  bus.on(EVENTS.GRAB, ({ kind } = {}) => play('grab', { kind }));
  bus.on(EVENTS.RELEASE, ({ velocity } = {}) => {
    // A throw already gets a whoosh from THROW; only a limp drop clicks.
    const speed = velocity && typeof velocity.length === 'function' ? velocity.length() : 0;
    if (speed < THROW_SPEED) play('release', {});
  });
  bus.on(EVENTS.THROW, ({ speed, stallId } = {}) => play('throw-whoosh', { speed, stallId }));

  // ── Scoring and payout ───────────────────────────────────────────────────
  bus.on(EVENTS.STALL_SCORE, ({ stallId, points, total, label } = {}) => {
    play('score-chime', { points, total, stallId, label });
  });
  bus.on(EVENTS.STALL_ROUND_END, ({ stallId, score, tokens } = {}) => {
    play('ticket-tally', { stallId, score, count: tallyTicks(score) });
  });
  bus.on(EVENTS.TOKENS_AWARDED, ({ amount, reason } = {}) => {
    play('token-award', { amount, reason });
  });
  bus.on(EVENTS.TICKETS_SPENT, ({ amount, reason } = {}) => {
    play('coin-insert', { amount, reason });
  });
  bus.on(EVENTS.INSUFFICIENT_TICKETS, ({ needed, have } = {}) => {
    play('insufficient-tickets', { needed, have });
  });
  bus.on(EVENTS.WALLET_CHANGE, ({ reason, delta } = {}) => {
    // Stars are the rarest currency; they get the shimmer, nothing else does.
    if (delta && delta.stars > 0) play('token-award', { reason, stars: delta.stars });
  });

  // ── Prizes ───────────────────────────────────────────────────────────────
  bus.on(EVENTS.DIRECT_PRIZE, ({ kind, stallId, variant } = {}) => {
    if (kind === 'fish') play('fish-win', { kind, stallId, variant });
    else play('prize-tier', { kind, stallId, variant });
  });

  // ── Movement and scene ───────────────────────────────────────────────────
  bus.on(EVENTS.TELEPORT, ({ nodeId } = {}) => {
    play('teleport', { nodeId });
    if (nodeId === 'personal-space') ambientMod.start('personal-space');
  });
  bus.on(EVENTS.STALL_ACTIVATE, () => ambientMod.start('stall'));
  bus.on(EVENTS.STALL_EXIT, () => ambientMod.start('hub'));
  bus.on(EVENTS.UI_TOAST, ({ kind } = {}) => {
    play(kind === 'bad' ? 'toast-bad' : 'toast-good', { kind });
  });
}

/** @private Longer payouts rattle for longer. */
function tallyTicks(score) {
  const s = Number(score) || 0;
  return Math.max(6, Math.min(24, Math.round(6 + s / 25)));
}

/**
 * Extend `window.__cc` with the audio surface.
 *
 * Attached here rather than in `core/testmode.js` deliberately: `testmode.js`
 * is shared by every agent and this needs no changes there, since
 * `testmode.install()` runs at main.js:56, well before `audio.install()`.
 * @private
 */
function installTestApi() {
  if (typeof window === 'undefined' || !window.__cc) return;
  window.__cc.audio = {
    /** Every play request, in order: `{ name, opts, t }`. */
    played: () => ctxmod.played(),
    /** Recipe names played, `@`-prefixed control entries excluded. */
    names: () => ctxmod.played().map((e) => e.name).filter((n) => !n.startsWith('@')),
    clear: () => { ctxmod.clearLog(); return true; },

    state: () => ({
      ...ctxmod.snapshot(),
      ambient: ambientMod.snapshot(),
      music: musicMod.snapshot(),
      spatial: spatial.snapshot(),
      sustained: [...sustained.keys()],
    }),

    recipes: () => recipeNames(),
    aliases: () => aliasNames(),
    materials: () => materialNames(),
    tracks: () => musicMod.trackNames(),
    scenes: () => ambientMod.sceneNames(),
    /** Does a name — canonical, GDD or shorthand — resolve to a recipe? */
    resolve: (name) => resolve(name),

    play: (name, opts) => { play(name, opts); return true; },
    stop: (name) => { audio.stop(name); return true; },
    setVolume: (channel, v) => { ctxmod.setVolume(channel, v); return true; },
    volumes: () => ctxmod.volumes(),
    unlock: () => ctxmod.unlock(),
    isReady: () => ctxmod.isReady(),
    setMuted: (m) => { ctxmod.setMuted(m); return true; },
    isMuted: () => ctxmod.isMuted(),

    ambient: {
      start: (scene) => { ambientMod.start(scene); return true; },
      stop: () => { ambientMod.stop(); return true; },
      state: () => ambientMod.snapshot(),
    },
    music: {
      play: (id, opts) => { musicMod.play(id, opts); return true; },
      stop: (fade) => { musicMod.stop(fade); return true; },
      state: () => musicMod.snapshot(),
    },
    spatial: {
      resolve: (position) => spatial.resolve(position),
      listener: () => spatial.listenerPosition(),
      emitters: () => spatial.emitterIds(),
    },

    /** Render a recipe offline and measure it — proof it is not silent. */
    renderOffline: (name, opts) => renderOffline(name, opts),
    renderAll: (opts) => renderAll(opts),
  };
}

/**
 * Bring the audio engine up. Called once by `main.js`.
 *
 * Nothing audible happens until the first user gesture — that is the autoplay
 * policy, not a limitation — and under `?test=1` nothing audible happens at all.
 */
export function install() {
  if (installed) return;
  installed = true;

  ctxmod.installGestureUnlock();
  ambientMod.setPlayFn(play);
  spatial.install({ play });
  wireEvents();
  installTestApi();
}

export default { audio, ambient, music, install };
